// src/components/PeriodTimesCard.jsx
// 講時の時刻（教務）【2026-09-18】
//
//   名簿の「月1」から曜日時限を作るときに、1限が何時から何時までかが要る。
//   もとは取り込みコードに龍谷大学の講時がベタ書きだったので、
//   大学ごとにここで決められるようにした。
//   時刻は直接打てる（type="time"）。行は足したり減らしたりできる。
import React, { useEffect, useState } from 'react';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' });
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export default function PeriodTimesCard() {
  const [rows, setRows]   = useState([]);
  const [canEdit, setCan] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState('');
  const [err, setErr]     = useState('');

  const load = async () => {
    try {
      const r = await fetch('/api/periods', { headers: auth() });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || '講時を取得できませんでした');
      setRows(d.periods.map(p => ({ ...p })));
      setCan(!!d.canEdit);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); }, []);

  const set = (i, k, v) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => {
    const next = rows.length ? Math.max(...rows.map(r => Number(r.periodNo))) + 1 : 1;
    const last = rows[rows.length - 1];
    setRows(rs => [...rs, {
      periodNo: next, label: '',
      startTime: last ? last.endTime : '09:15',
      endTime:   last ? last.endTime : '10:45',
    }]);
  };
  const delRow = (i) => setRows(rs => rs.filter((_, j) => j !== i));

  const bad = (() => {
    const nos = rows.map(r => Number(r.periodNo));
    if (rows.length === 0) return '1コマ以上必要です';
    if (new Set(nos).size !== nos.length) return '同じ講時の番号が2つあります';
    for (const r of rows) {
      if (!Number.isInteger(Number(r.periodNo))) return '講時の番号は数字で入れてください';
      if (!TIME_RE.test(r.startTime) || !TIME_RE.test(r.endTime)) return `${r.periodNo}限の時刻を入れてください`;
      if (r.startTime >= r.endTime) return `${r.periodNo}限は終わりを始まりより後にしてください`;
    }
    return '';
  })();

  const save = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch('/api/periods', {
        method: 'PUT', headers: auth(),
        body: JSON.stringify({
          periods: rows.map(x => ({
            periodNo: Number(x.periodNo),
            label: x.label,
            startTime: x.startTime,
            endTime: x.endTime,
          })),
        }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || '保存できませんでした');
      setMsg(d.message);
      setRows(d.periods.map(p => ({ ...p })));
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" data-period-times>
      <div className="card-header"><h2>講時の時刻</h2></div>
      <p style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 0 }}>
        名簿に入っている「月1」「火2」を曜日時限に直すときに、ここの時刻を使います。
        授業スケジュールを名簿から自動で作る場合と、遅刻の判定に効きます。
      </p>
      {err && <p style={{ color: '#b91c1c' }}>{err}</p>}
      {msg && <p style={{ color: '#166534' }}>{msg}</p>}

      <table className="data-table">
        <thead>
          <tr><th>講時</th><th>表示名</th><th>始まり</th><th>終わり</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                <input type="text" inputMode="numeric" value={r.periodNo} aria-label="講時の番号"
                       disabled={!canEdit}
                       onChange={e => set(i, 'periodNo', e.target.value.replace(/[^0-9]/g, ''))}
                       style={{ width: '3.2em', padding: '0.3rem', textAlign: 'center' }} />
                <span style={{ marginLeft: '0.25rem' }}>限</span>
              </td>
              <td>
                <input type="text" value={r.label || ''} placeholder={`${r.periodNo}限`}
                       aria-label="表示名" disabled={!canEdit}
                       onChange={e => set(i, 'label', e.target.value)}
                       style={{ width: '7em', padding: '0.3rem' }} />
              </td>
              <td>
                <input type="time" value={r.startTime} aria-label={`${r.periodNo}限の始まり`}
                       disabled={!canEdit} onChange={e => set(i, 'startTime', e.target.value)} />
              </td>
              <td>
                <input type="time" value={r.endTime} aria-label={`${r.periodNo}限の終わり`}
                       disabled={!canEdit} onChange={e => set(i, 'endTime', e.target.value)} />
              </td>
              <td>
                {canEdit && (
                  <button className="btn-secondary" onClick={() => delRow(i)}>削除</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {canEdit && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn-secondary" onClick={addRow}>コマを追加</button>
          <button className="btn-primary" onClick={save} disabled={busy || !!bad}>
            {busy ? '保存中…' : '保存'}
          </button>
          {bad && <small style={{ color: '#b91c1c' }}>{bad}</small>}
        </div>
      )}
      {!canEdit && (
        <p style={{ fontSize: '0.82rem', color: '#9ca3af' }}>変更できるのは教務だけです。</p>
      )}
    </div>
  );
}
